// @ts-nocheck
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import notify from '../utils/toast';
import { useI18n } from '../context/I18nContext';

const TABS = [
    { id: 'fy', label: 'Financial Year' },
    { id: 'tb', label: 'Trial Balance' },
    { id: 'ie', label: 'Income & Expenditure' },
    { id: 'bs', label: 'Balance Sheet' },
    { id: 'rp', label: 'Receipts & Payments' },
    { id: 'cf', label: 'Cash Flow' },
    { id: 'ledger', label: 'Account Ledger' },
    { id: 'cust', label: 'Customer Ledger' },
    { id: 'supp', label: 'Supplier Ledger' },
    { id: 'party', label: 'Party Balances' },
    { id: 'journal', label: 'Journal Entries' },
];

export default function AccountingReports() {
    const { lang, t } = useI18n();
    const [tab, setTab] = useState('tb');
    const [projects, setProjects] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [projectId, setProjectId] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [accountId, setAccountId] = useState('');
    const [data, setData] = useState(null);
    const [summary, setSummary] = useState(null);
    const [openForm, setOpenForm] = useState({ party: 'customer', id: '', opening_balance: '', opening_balance_type: 'debit', opening_balance_date: '' });
    const [years, setYears] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fyForm, setFyForm] = useState({ name: '', start_date: '', end_date: '', is_current: true });
    const [journalForm, setJournalForm] = useState({
        entry_date: new Date().toISOString().slice(0, 10),
        narration: '',
        project_id: '',
        lines: [
            { account_id: '', debit: '', credit: '' },
            { account_id: '', debit: '', credit: '' },
        ],
    });

    useEffect(() => {
        api.get('/projects').then((r) => setProjects(r.data.data)).catch(() => {});
        api.get('/accounts/chart').then((r) => setAccounts(r.data.data)).catch(() => {});
        api.get('/accounting/financial-years').then((r) => setYears(r.data.data)).catch(() => {});
    }, []);

    const params = () => {
        const p = {};
        if (projectId) p.project_id = projectId;
        if (from) p.from = from;
        if (to) p.to = to;
        return p;
    };

    const loadTab = async () => {
        setLoading(true);
        setData(null);
        try {
            let res;
            if (tab === 'tb') res = await api.get('/accounting/trial-balance', { params: params() });
            else if (tab === 'ie') res = await api.get('/accounting/income-expenditure', { params: params() });
            else if (tab === 'bs') res = await api.get('/accounting/balance-sheet', { params: params() });
            else if (tab === 'rp') res = await api.get('/accounting/receipts-payments', { params: params() });
            else if (tab === 'cf') res = await api.get('/accounting/cash-flow', { params: params() });
            else if (tab === 'ledger') {
                if (!accountId) { setLoading(false); return; }
                res = await api.get('/accounting/ledger', { params: { ...params(), account_id: accountId } });
            } else if (tab === 'cust') res = await api.get('/accounting/customer-ledger', { params: params() });
            else if (tab === 'supp') res = await api.get('/accounting/supplier-ledger', { params: params() });
            else if (tab === 'journal') res = await api.get('/accounting/journal', { params: params() });
            else if (tab === 'fy') {
                res = await api.get('/accounting/financial-years');
                setYears(res.data.data);
                setLoading(false);
                return;
            }
            setData(res?.data?.data);
            setSummary(res?.data?.summary || res?.data?.data?.summary || null);
        } catch (err) {
            notify.apiError(err);
        } finally {
            setLoading(false);
        }
    };

    const saveOpening = async (e) => {
        e.preventDefault();
        try {
            if (openForm.party === 'customer') {
                await api.post('/accounting/customer-opening', {
                    customer_id: Number(openForm.id),
                    opening_balance: Number(openForm.opening_balance || 0),
                    opening_balance_type: openForm.opening_balance_type,
                    opening_balance_date: openForm.opening_balance_date || null,
                });
            } else {
                await api.post('/accounting/supplier-opening', {
                    supplier_id: Number(openForm.id),
                    opening_balance: Number(openForm.opening_balance || 0),
                    opening_balance_type: openForm.opening_balance_type,
                    opening_balance_date: openForm.opening_balance_date || null,
                });
            }
            notify.info('Opening balance saved');
            loadTab();
        } catch (err) {
            notify.apiError(err);
        }
    };


    useEffect(() => {
        if (tab !== 'fy') loadTab();
        else loadTab();
    }, [tab]);

    const createFY = async (e) => {
        e.preventDefault();
        try {
            await api.post('/accounting/financial-years', fyForm);
            setFyForm({ name: '', start_date: '', end_date: '', is_current: true });
            loadTab();
        } catch (err) {
            notify.apiError(err);
        }
    };

    const createJournal = async (e) => {
        e.preventDefault();
        try {
            await api.post('/accounting/journal', {
                ...journalForm,
                project_id: journalForm.project_id || null,
                lines: journalForm.lines.map((l) => ({
                    account_id: Number(l.account_id),
                    debit: Number(l.debit || 0),
                    credit: Number(l.credit || 0),
                })),
            });
            notify.success('Saved successfully');
            loadTab();
        } catch (err) {
            notify.apiError(err);
        }
    };

    return (
        <div>
            <h4 className="mb-1">{t('professionalAccountsReports')}</h4>
            <p className="text-muted mb-3">
                {t('accountsReportsDesc')}
            </p>

            <div className="card table-card mb-3">
                <div className="card-body py-2 d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label small mb-0">{t('project')}</label>
                        <select className="form-select form-select-sm" style={{ width: 200 }} value={projectId} onChange={(e) => setProjectId(e.target.value)}>
                            <option value="">{t('allProjects')}</option>
                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="form-label small mb-0">From</label>
                        <input type="date" className="form-control form-control-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
                    </div>
                    <div>
                        <label className="form-label small mb-0">To</label>
                        <input type="date" className="form-control form-control-sm" value={to} onChange={(e) => setTo(e.target.value)} />
                    </div>
                    {tab === 'ledger' && (
                        <div>
                            <label className="form-label small mb-0">Account</label>
                            <select className="form-select form-select-sm" style={{ width: 220 }} value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                                <option value="">—</option>
                                {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                            </select>
                        </div>
                    )}
                    <button className="btn btn-primary btn-sm" onClick={loadTab}>{t('runReport')}</button>
                </div>
            </div>

            <div className="d-flex flex-wrap gap-1 mb-3">
                {TABS.map((t) => (
                    <button key={t.id} type="button" className={`btn btn-sm ${tab === t.id ? 'btn-primary' : 'btn-outline-secondary'}`} onClick={() => setTab(t.id)}>
                        {t.label}
                    </button>
                ))}
            </div>

            {loading && <div className="text-center py-4"><div className="spinner-border text-success" /></div>}

            {!loading && tab === 'fy' && (
                <div className="row g-3">
                    <div className="col-md-5">
                        <div className="card table-card">
                            <div className="card-header fw-semibold">Create Financial Year</div>
                            <div className="card-body">
                                <form onSubmit={createFY}>
                                    <div className="mb-2"><input className="form-control" placeholder="Name e.g. FY 2025-26" required value={fyForm.name} onChange={(e) => setFyForm({ ...fyForm, name: e.target.value })} /></div>
                                    <div className="mb-2"><input type="date" className="form-control" required value={fyForm.start_date} onChange={(e) => setFyForm({ ...fyForm, start_date: e.target.value })} /></div>
                                    <div className="mb-2"><input type="date" className="form-control" required value={fyForm.end_date} onChange={(e) => setFyForm({ ...fyForm, end_date: e.target.value })} /></div>
                                    <div className="form-check mb-2">
                                        <input className="form-check-input" type="checkbox" checked={fyForm.is_current} onChange={(e) => setFyForm({ ...fyForm, is_current: e.target.checked })} id="curFy" />
                                        <label className="form-check-label" htmlFor="curFy">Set as current</label>
                                    </div>
                                    <button className="btn btn-primary btn-sm">Save</button>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-7">
                        <div className="card table-card">
                            <div className="table-responsive">
                                <table className="table mb-0">
                                    <thead className="table-light"><tr><th>Name</th><th>Start</th><th>End</th><th>Current</th></tr></thead>
                                    <tbody>
                                        {years.map((y) => (
                                            <tr key={y.id}>
                                                <td>{y.name}</td>
                                                <td>{y.start_date}</td>
                                                <td>{y.end_date}</td>
                                                <td>{y.is_current ? <span className="badge bg-success">Current</span> : '—'}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && tab === 'tb' && data && (
                <div className="card table-card">
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead className="table-light"><tr><th>Code</th><th>Account</th><th>Type</th><th className="text-end">Debit</th><th className="text-end">Credit</th></tr></thead>
                            <tbody>
                                {data.rows?.map((r) => (
                                    <tr key={r.account_id}><td>{r.code}</td><td>{r.name}</td><td className="text-capitalize">{r.type}</td>
                                        <td className="text-end">{r.debit ? r.debit.toLocaleString() : '—'}</td>
                                        <td className="text-end">{r.credit ? r.credit.toLocaleString() : '—'}</td></tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="fw-bold"><td colSpan={3}>Total</td>
                                    <td className="text-end">{data.totalDebit?.toLocaleString()}</td>
                                    <td className="text-end">{data.totalCredit?.toLocaleString()}</td></tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            )}

            {!loading && tab === 'ie' && data && (
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-header fw-semibold text-success">Income</div>
                            <ul className="list-group list-group-flush">
                                {data.income?.map((r, i) => (
                                    <li key={i} className="list-group-item d-flex justify-content-between"><span>{r.code} — {r.name}</span><span>৳ {r.amount.toLocaleString()}</span></li>
                                ))}
                                <li className="list-group-item d-flex justify-content-between fw-bold"><span>Total Income</span><span>৳ {data.totalIncome?.toLocaleString()}</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-header fw-semibold text-danger">Expenditure</div>
                            <ul className="list-group list-group-flush">
                                {data.expense?.map((r, i) => (
                                    <li key={i} className="list-group-item d-flex justify-content-between"><span>{r.code} — {r.name}</span><span>৳ {r.amount.toLocaleString()}</span></li>
                                ))}
                                <li className="list-group-item d-flex justify-content-between fw-bold"><span>Total Expense</span><span>৳ {data.totalExpense?.toLocaleString()}</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="col-12">
                        <div className="alert alert-info mb-0">
                            Surplus / (Deficit): <strong>৳ {data.surplus?.toLocaleString()}</strong>
                        </div>
                    </div>
                </div>
            )}

            {!loading && tab === 'bs' && data && (
                <div className="row g-3">
                    <div className="col-md-4">
                        <div className="card table-card"><div className="card-header fw-semibold">Assets</div>
                            <ul className="list-group list-group-flush">
                                {data.assets?.map((r, i) => <li key={i} className="list-group-item d-flex justify-content-between"><span>{r.code} {r.name}</span><span>৳ {r.amount.toLocaleString()}</span></li>)}
                                <li className="list-group-item fw-bold d-flex justify-content-between"><span>Total</span><span>৳ {data.totalAssets?.toLocaleString()}</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card table-card"><div className="card-header fw-semibold">Liabilities</div>
                            <ul className="list-group list-group-flush">
                                {data.liabilities?.map((r, i) => <li key={i} className="list-group-item d-flex justify-content-between"><span>{r.code} {r.name}</span><span>৳ {r.amount.toLocaleString()}</span></li>)}
                                <li className="list-group-item fw-bold d-flex justify-content-between"><span>Total</span><span>৳ {data.totalLiabilities?.toLocaleString()}</span></li>
                            </ul>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className="card table-card"><div className="card-header fw-semibold">Equity</div>
                            <ul className="list-group list-group-flush">
                                {data.equity?.map((r, i) => <li key={i} className="list-group-item d-flex justify-content-between"><span>{r.code} {r.name}</span><span>৳ {r.amount.toLocaleString()}</span></li>)}
                                <li className="list-group-item fw-bold d-flex justify-content-between"><span>Total</span><span>৳ {data.totalEquity?.toLocaleString()}</span></li>
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {!loading && tab === 'rp' && data && (
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-header fw-semibold text-success">Receipts — ৳ {data.totalReceipts?.toLocaleString()}</div>
                            <div className="table-responsive" style={{ maxHeight: 360 }}>
                                <table className="table table-sm mb-0">
                                    <thead><tr><th>Date</th><th>Particular</th><th className="text-end">Amount</th></tr></thead>
                                    <tbody>
                                        {data.receipts?.map((r, i) => (
                                            <tr key={i}><td>{r.date}</td><td>{r.particular}</td><td className="text-end">{r.amount.toLocaleString()}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="card table-card">
                            <div className="card-header fw-semibold text-danger">Payments — ৳ {data.totalPayments?.toLocaleString()}</div>
                            <div className="table-responsive" style={{ maxHeight: 360 }}>
                                <table className="table table-sm mb-0">
                                    <thead><tr><th>Date</th><th>Particular</th><th className="text-end">Amount</th></tr></thead>
                                    <tbody>
                                        {data.payments?.map((r, i) => (
                                            <tr key={i}><td>{r.date}</td><td>{r.particular}</td><td className="text-end">{r.amount.toLocaleString()}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                    <div className="col-12"><div className="alert alert-secondary">Closing: <strong>৳ {data.closing?.toLocaleString()}</strong></div></div>
                </div>
            )}

            {!loading && tab === 'cf' && data && (
                <div className="card table-card" style={{ maxWidth: 480 }}>
                    <div className="card-body">
                        <div className="d-flex justify-content-between mb-2"><span>Operating Inflows</span><strong className="text-success">৳ {data.operatingInflows?.toLocaleString()}</strong></div>
                        <div className="d-flex justify-content-between mb-2"><span>Operating Outflows</span><strong className="text-danger">৳ {data.operatingOutflows?.toLocaleString()}</strong></div>
                        <hr />
                        <div className="d-flex justify-content-between"><span>Net Cash Flow</span><strong>৳ {data.netCashFlow?.toLocaleString()}</strong></div>
                        <p className="small text-muted mt-3 mb-0">{data.note}</p>
                    </div>
                </div>
            )}

            {!loading && tab === 'ledger' && data && (
                <div className="card table-card">
                    <div className="card-header fw-semibold">{data.account?.code} — {data.account?.name}</div>
                    <div className="table-responsive">
                        <table className="table table-sm mb-0">
                            <thead className="table-light"><tr><th>Date</th><th>Entry</th><th>Narration</th><th className="text-end">Debit</th><th className="text-end">Credit</th><th className="text-end">Balance</th></tr></thead>
                            <tbody>
                                {data.rows?.map((r, i) => (
                                    <tr key={i}><td>{r.date}</td><td>{r.entry_no}</td><td>{r.narration}</td>
                                        <td className="text-end">{r.debit || '—'}</td><td className="text-end">{r.credit || '—'}</td>
                                        <td className="text-end">{r.balance?.toLocaleString()}</td></tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {!loading && tab === 'cust' && data && (
                <div>
                    {summary && (
                        <div className="alert alert-light border mb-3 small">
                            Opening Dr: <strong>৳ {(summary.total_opening_debit || 0).toLocaleString()}</strong>
                            {' · '}Opening Cr: <strong>৳ {(summary.total_opening_credit || 0).toLocaleString()}</strong>
                            {' · '}Closing Receivable: <strong className="text-danger">৳ {(summary.total_closing_receivable || 0).toLocaleString()}</strong>
                            {' · '}Customer Advance: <strong className="text-success">৳ {(summary.total_closing_advance || 0).toLocaleString()}</strong>
                        </div>
                    )}
                    <div className="row g-3">
                        {(Array.isArray(data) ? data : []).map((row) => (
                            <div className="col-lg-6" key={row.customer_id}>
                                <div className="card table-card h-100">
                                    <div className="card-header d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold">{row.customer?.name}</span>
                                        <span className={`badge bg-${row.closing_balance >= 0 ? 'danger' : 'success'}`}>
                                            Closing: ৳ {Math.abs(row.closing_balance).toLocaleString()} {row.closing_balance_type === 'debit' ? 'Dr (Due)' : 'Cr (Advance)'}
                                        </span>
                                    </div>
                                    <div className="card-body">
                                        <div className="row small mb-2">
                                            <div className="col-6">Opening: ৳ {Number(row.opening_balance).toLocaleString()} <span className="text-muted">({row.opening_balance_type})</span></div>
                                            <div className="col-3 text-success">Paid: ৳ {Number(row.total_paid).toLocaleString()}</div>
                                            <div className="col-3">Sales: ৳ {Number(row.total_sales).toLocaleString()}</div>
                                        </div>
                                        <div className="table-responsive" style={{ maxHeight: 220 }}>
                                            <table className="table table-sm mb-0">
                                                <thead className="table-light"><tr><th>Date</th><th>Particular</th><th className="text-end">Dr</th><th className="text-end">Cr</th><th className="text-end">Bal</th></tr></thead>
                                                <tbody>
                                                    {(row.movements || []).map((m, i) => (
                                                        <tr key={i}>
                                                            <td>{m.date || '—'}</td>
                                                            <td>{m.particular}</td>
                                                            <td className="text-end">{m.debit ? m.debit.toLocaleString() : '—'}</td>
                                                            <td className="text-end">{m.credit ? m.credit.toLocaleString() : '—'}</td>
                                                            <td className="text-end">{Number(m.balance).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!loading && tab === 'supp' && data && (
                <div>
                    {summary && (
                        <div className="alert alert-light border mb-3 small">
                            Opening Cr (Payable): <strong>৳ {(summary.total_opening_credit || 0).toLocaleString()}</strong>
                            {' · '}Closing Payable: <strong className="text-danger">৳ {(summary.total_closing_payable || 0).toLocaleString()}</strong>
                            {' · '}Advance to supplier: <strong className="text-success">৳ {(summary.total_closing_advance || 0).toLocaleString()}</strong>
                        </div>
                    )}
                    <div className="row g-3">
                        {(Array.isArray(data) ? data : []).map((s) => (
                            <div className="col-lg-6" key={s.supplier_id}>
                                <div className="card table-card h-100">
                                    <div className="card-header d-flex justify-content-between">
                                        <span className="fw-semibold">{s.supplier?.name}</span>
                                        <span className={`badge bg-${s.closing_balance >= 0 ? 'warning text-dark' : 'success'}`}>
                                            Closing: ৳ {Math.abs(s.closing_balance).toLocaleString()} {s.closing_balance_type === 'credit' ? 'Cr (Payable)' : 'Dr (Advance)'}
                                        </span>
                                    </div>
                                    <div className="card-body">
                                        <div className="small mb-2">
                                            Opening: ৳ {Number(s.opening_balance).toLocaleString()} ({s.opening_balance_type})
                                            {' · '}Purchases: ৳ {Number(s.total_purchases).toLocaleString()}
                                        </div>
                                        <div className="table-responsive" style={{ maxHeight: 220 }}>
                                            <table className="table table-sm mb-0">
                                                <thead className="table-light"><tr><th>Date</th><th>Particular</th><th className="text-end">Dr</th><th className="text-end">Cr</th><th className="text-end">Bal</th></tr></thead>
                                                <tbody>
                                                    {(s.movements || []).map((m, i) => (
                                                        <tr key={i}>
                                                            <td>{m.date || '—'}</td>
                                                            <td>{m.particular}</td>
                                                            <td className="text-end">{m.debit ? Number(m.debit).toLocaleString() : '—'}</td>
                                                            <td className="text-end">{m.credit ? Number(m.credit).toLocaleString() : '—'}</td>
                                                            <td className="text-end">{Number(m.balance).toLocaleString()}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!loading && tab === 'party' && data && (
                <div>
                    <div className="row g-3 mb-3">
                        <div className="col-md-3"><div className="card stat-card"><div className="card-body small">Customers receivable<br/><strong className="fs-5 text-danger">৳ {(data.summary?.customers_receivable || 0).toLocaleString()}</strong></div></div></div>
                        <div className="col-md-3"><div className="card stat-card"><div className="card-body small">Customer advances<br/><strong className="fs-5 text-success">৳ {(data.summary?.customers_advance || 0).toLocaleString()}</strong></div></div></div>
                        <div className="col-md-3"><div className="card stat-card"><div className="card-body small">Suppliers payable<br/><strong className="fs-5 text-warning">৳ {(data.summary?.suppliers_payable || 0).toLocaleString()}</strong></div></div></div>
                        <div className="col-md-3"><div className="card stat-card"><div className="card-body small">Supplier advances<br/><strong className="fs-5">৳ {(data.summary?.suppliers_advance || 0).toLocaleString()}</strong></div></div></div>
                    </div>

                    <div className="card table-card mb-3">
                        <div className="card-header fw-semibold">Set Opening Balance</div>
                        <div className="card-body">
                            <form className="row g-2 align-items-end" onSubmit={saveOpening}>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Party</label>
                                    <select className="form-select form-select-sm" value={openForm.party} onChange={(e) => setOpenForm({ ...openForm, party: e.target.value, opening_balance_type: e.target.value === 'customer' ? 'debit' : 'credit' })}>
                                        <option value="customer">Customer</option>
                                        <option value="supplier">Supplier</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label small mb-0">Select</label>
                                    <select className="form-select form-select-sm" required value={openForm.id} onChange={(e) => setOpenForm({ ...openForm, id: e.target.value })}>
                                        <option value="">—</option>
                                        {openForm.party === 'customer'
                                            ? (data.customers || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)
                                            : (data.suppliers || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Amount</label>
                                    <input type="number" className="form-control form-control-sm" required value={openForm.opening_balance} onChange={(e) => setOpenForm({ ...openForm, opening_balance: e.target.value })} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Type</label>
                                    <select className="form-select form-select-sm" value={openForm.opening_balance_type} onChange={(e) => setOpenForm({ ...openForm, opening_balance_type: e.target.value })}>
                                        <option value="debit">Debit (Dr)</option>
                                        <option value="credit">Credit (Cr)</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label small mb-0">Date</label>
                                    <input type="date" className="form-control form-control-sm" value={openForm.opening_balance_date} onChange={(e) => setOpenForm({ ...openForm, opening_balance_date: e.target.value })} />
                                </div>
                                <div className="col-md-1">
                                    <button type="submit" className="btn btn-primary btn-sm w-100">Save</button>
                                </div>
                            </form>
                            <p className="small text-muted mb-0 mt-2">
                                Customer Dr = receivable opening · Customer Cr = advance opening · Supplier Cr = payable opening · Supplier Dr = advance to supplier.
                            </p>
                        </div>
                    </div>

                    <div className="row g-3">
                        <div className="col-md-6">
                            <div className="card table-card">
                                <div className="card-header fw-semibold">Customers — Opening & Closing</div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead className="table-light"><tr><th>Name</th><th className="text-end">Opening</th><th className="text-end">Closing</th></tr></thead>
                                        <tbody>
                                            {(data.customers || []).map((c) => (
                                                <tr key={c.id}>
                                                    <td>{c.name}</td>
                                                    <td className="text-end">{Number(c.opening_balance).toLocaleString()} {c.opening_balance_type}</td>
                                                    <td className="text-end fw-semibold">{Number(Math.abs(c.closing_balance)).toLocaleString()} {c.closing_balance_type}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="card table-card">
                                <div className="card-header fw-semibold">Suppliers — Opening & Closing</div>
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead className="table-light"><tr><th>Name</th><th className="text-end">Opening</th><th className="text-end">Closing</th></tr></thead>
                                        <tbody>
                                            {(data.suppliers || []).map((s) => (
                                                <tr key={s.id}>
                                                    <td>{s.name}</td>
                                                    <td className="text-end">{Number(s.opening_balance).toLocaleString()} {s.opening_balance_type}</td>
                                                    <td className="text-end fw-semibold">{Number(Math.abs(s.closing_balance)).toLocaleString()} {s.closing_balance_type}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {!loading && tab === 'journal' && (
                <div className="row g-3">
                    <div className="col-lg-5">
                        <div className="card table-card">
                            <div className="card-header fw-semibold">New Journal Entry</div>
                            <div className="card-body">
                                <form onSubmit={createJournal}>
                                    <div className="mb-2"><input type="date" className="form-control form-control-sm" value={journalForm.entry_date} onChange={(e) => setJournalForm({ ...journalForm, entry_date: e.target.value })} /></div>
                                    <div className="mb-2">
                                        <select className="form-select form-select-sm" value={journalForm.project_id} onChange={(e) => setJournalForm({ ...journalForm, project_id: e.target.value })}>
                                            <option value="">Project (optional)</option>
                                            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="mb-2"><input className="form-control form-control-sm" placeholder="Narration" value={journalForm.narration} onChange={(e) => setJournalForm({ ...journalForm, narration: e.target.value })} /></div>
                                    {journalForm.lines.map((l, idx) => (
                                        <div className="row g-1 mb-1" key={idx}>
                                            <div className="col-6">
                                                <select className="form-select form-select-sm" required value={l.account_id} onChange={(e) => {
                                                    const lines = [...journalForm.lines];
                                                    lines[idx] = { ...lines[idx], account_id: e.target.value };
                                                    setJournalForm({ ...journalForm, lines });
                                                }}>
                                                    <option value="">Account</option>
                                                    {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
                                                </select>
                                            </div>
                                            <div className="col-3">
                                                <input type="number" className="form-control form-control-sm" placeholder="Dr" value={l.debit} onChange={(e) => {
                                                    const lines = [...journalForm.lines];
                                                    lines[idx] = { ...lines[idx], debit: e.target.value };
                                                    setJournalForm({ ...journalForm, lines });
                                                }} />
                                            </div>
                                            <div className="col-3">
                                                <input type="number" className="form-control form-control-sm" placeholder="Cr" value={l.credit} onChange={(e) => {
                                                    const lines = [...journalForm.lines];
                                                    lines[idx] = { ...lines[idx], credit: e.target.value };
                                                    setJournalForm({ ...journalForm, lines });
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" className="btn btn-link btn-sm px-0" onClick={() => setJournalForm({ ...journalForm, lines: [...journalForm.lines, { account_id: '', debit: '', credit: '' }] })}>+ Line</button>
                                    <button type="submit" className="btn btn-primary btn-sm d-block mt-2">Post Entry</button>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-7">
                        <div className="card table-card">
                            <div className="table-responsive">
                                <table className="table table-sm mb-0">
                                    <thead className="table-light"><tr><th>No</th><th>Date</th><th>Narration</th><th className="text-end">Amount</th></tr></thead>
                                    <tbody>
                                        {(data || []).map((e) => (
                                            <tr key={e.id}><td>{e.entry_no}</td><td>{e.entry_date}</td><td>{e.narration}</td><td className="text-end">{Number(e.total_debit).toLocaleString()}</td></tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
